/**
 * Cron ifadelerini Turkce, insan-okunaklı bir metne cevirir.
 *
 * Hangfire/Cronos'un kabul ettigi 5-alanli standart cron'u parse eder
 * (min hour day month dow). En sik kullanilan ornekleri ozel olarak
 * ele alır (her gun X, haftada bir, ayin N'i vb.); kompleks ifadelerde
 * ham cron'a fallback yapar.
 */

const DAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function pad(n: number) {
  return n.toString().padStart(2, '0');
}

function formatTime(min: string, hour: string): string | null {
  const m = parseInt(min, 10);
  const h = parseInt(hour, 10);
  if (Number.isNaN(m) || Number.isNaN(h)) return null;
  if (m < 0 || m > 59 || h < 0 || h > 23) return null;
  return `${pad(h)}:${pad(m)}`;
}

/**
 * "0 22 * * *" -> "Her gun saat 22:00"
 * "0 9 * * 1" -> "Her Pazartesi saat 09:00"
 * "0 9 1 * *" -> "Her ayin 1'i saat 09:00"
 * "30 8 * * 1-5" -> "Hafta ici (Pzt-Cum) saat 08:30"
 */
export function humanizeCron(cron: string): string {
  if (!cron) return '';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [min, hour, dom, mon, dow] = parts;
  const time = formatTime(min, hour);
  if (!time) return cron;

  const isAnyDom = dom === '*';
  const isAnyMon = mon === '*';
  const isAnyDow = dow === '*';

  if (isAnyDom && isAnyMon && isAnyDow) {
    return `Her gün saat ${time}`;
  }

  if (isAnyDom && isAnyMon && !isAnyDow) {
    if (dow === '1-5') return `Hafta içi (Pzt-Cum) saat ${time}`;
    if (dow === '0,6' || dow === '6,0') return `Hafta sonu (Cmt-Paz) saat ${time}`;

    const dayList = parseList(dow, 0, 6);
    if (dayList && dayList.length > 0) {
      const names = dayList.map((d) => DAY_NAMES[d % 7]).join(', ');
      return `Her ${names} saat ${time}`;
    }
    return `Cron: ${cron}`;
  }

  if (!isAnyDom && isAnyMon && isAnyDow) {
    const domList = parseList(dom, 1, 31);
    if (domList && domList.length === 1) {
      return `Her ayın ${domList[0]}'i saat ${time}`;
    }
    if (domList && domList.length > 1) {
      const days = domList.join(', ');
      return `Her ayın ${days}. günleri saat ${time}`;
    }
    return `Cron: ${cron}`;
  }

  if (!isAnyDom && !isAnyMon && isAnyDow) {
    const domList = parseList(dom, 1, 31);
    const monList = parseList(mon, 1, 12);
    if (domList?.length === 1 && monList?.length === 1) {
      return `Her ${MONTH_NAMES[monList[0] - 1]} ${domList[0]} saat ${time}`;
    }
  }

  return `Cron: ${cron}`;
}

/**
 * Cron ifadesinden tipini cikarmaya calisir (insan okunakli ozet icin).
 * Sertifikali tahmin: gunluk/haftalik/aylik/ozel.
 */
export function inferCronCadence(cron: string): 'daily' | 'weekly' | 'monthly' | 'custom' {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return 'custom';
  const [, , dom, mon, dow] = parts;

  if (dom === '*' && mon === '*' && dow === '*') return 'daily';
  if (dom === '*' && mon === '*' && dow !== '*') return 'weekly';
  if (dom !== '*' && mon === '*' && dow === '*') return 'monthly';
  return 'custom';
}

function parseList(field: string, min: number, max: number): number[] | null {
  if (!field || field === '*') return null;
  const items = field.split(',').map((s) => s.trim());
  const out: number[] = [];
  for (const item of items) {
    if (/^\d+$/.test(item)) {
      const n = parseInt(item, 10);
      if (n >= min && n <= max) out.push(n);
      else return null;
    } else if (/^\d+-\d+$/.test(item)) {
      const [a, b] = item.split('-').map((s) => parseInt(s, 10));
      if (a < min || b > max || a > b) return null;
      for (let i = a; i <= b; i++) out.push(i);
    } else {
      return null;
    }
  }
  const dedup = Array.from(new Set(out));
  return dedup.sort((a, b) => a - b);
}
